/**
 * A custom element that renders a menu-driven market sector list inside
 * the market-selector drawer.
 *
 * Each list item carries a `data-url` attribute pointing to the market's
 * destination URL. Selecting an item enables the Continue button and updates
 * its label. Clicking Continue navigates to the selected URL.
 */
class MarketSectorList extends HTMLElement {
  /** @type {string | null} */
  #selectedUrl = null;

  connectedCallback() {
    this.querySelectorAll('[role="option"]').forEach((btn) => {
      btn.addEventListener('click', this.#selectItem);
      btn.addEventListener('keydown', this.#onItemKeyDown);
    });

    const continueBtn = this.querySelector('.market-selector__submit-btn');
    if (continueBtn) {
      continueBtn.addEventListener('click', this.#navigate);
    }

    const searchInput = this.querySelector('.country-filter__input');
    if (searchInput) {
      searchInput.addEventListener('input', this.#filterItems);
      searchInput.addEventListener('keydown', this.#onSearchKeyDown);
    }
  }

  disconnectedCallback() {
    this.querySelectorAll('[role="option"]').forEach((btn) => {
      btn.removeEventListener('click', this.#selectItem);
      btn.removeEventListener('keydown', this.#onItemKeyDown);
    });

    const continueBtn = this.querySelector('.market-selector__submit-btn');
    if (continueBtn) {
      continueBtn.removeEventListener('click', this.#navigate);
    }

    const searchInput = this.querySelector('.country-filter__input');
    if (searchInput) {
      searchInput.removeEventListener('input', this.#filterItems);
      searchInput.removeEventListener('keydown', this.#onSearchKeyDown);
    }
  }

  /**
   * Marks the clicked item as selected and enables the Continue button.
   *
   * @param {MouseEvent} event
   */
  #selectItem = (event) => {
    const btn = /** @type {HTMLButtonElement} */ (event.currentTarget);

    // Deselect all items.
    this.querySelectorAll('[role="option"]').forEach((item) => {
      item.setAttribute('aria-selected', 'false');
    });

    btn.setAttribute('aria-selected', 'true');
    this.#selectedUrl = btn.dataset.url ?? null;

    const continueBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.market-selector__submit-btn')
    );

    if (continueBtn) {
      continueBtn.removeAttribute('disabled');
      continueBtn.removeAttribute('aria-disabled');

      const labelTemplate = continueBtn.dataset.labelContinue ?? '';
      const marketName = btn.querySelector('.country')?.textContent?.trim() ?? '';

      if (labelTemplate && marketName) {
        continueBtn.textContent = labelTemplate.replace('__MARKET__', marketName);
      }
    }
  };

  /**
   * Moves focus between list items with arrow keys; ArrowUp from first item returns to search.
   *
   * @param {HTMLElement | null} currentItem - Currently focused item, or null when coming from search.
   * @param {1 | -1} direction
   */
  #moveFocus(currentItem, direction) {
    const items = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[role="option"]:not([hidden])'))
    );
    if (!items.length) return;

    if (!currentItem) {
      items[0].focus();
      return;
    }

    const index = items.indexOf(currentItem);
    const next = items[index + direction];

    if (next) {
      next.focus();
    } else if (direction === -1) {
      const searchInput = /** @type {HTMLElement | null} */ (this.querySelector('.country-filter__input'));
      searchInput?.focus();
    }
  }

  /**
   * Handles keydown on the search input — ArrowDown enters the list.
   *
   * @param {KeyboardEvent} event
   */
  #onSearchKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#moveFocus(null, 1);
    }
  };

  /**
   * Handles keydown on a list item — ArrowUp/Down navigate between items.
   *
   * @param {KeyboardEvent} event
   */
  #onItemKeyDown = (event) => {
    const btn = /** @type {HTMLElement} */ (event.currentTarget);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#moveFocus(btn, 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#moveFocus(btn, -1);
    }
  };

  /**
   * Navigates to the selected market URL.
   */
  #navigate = () => {
    if (this.#selectedUrl) {
      window.location.href = this.#selectedUrl;
    }
  };

  /**
   * Filters the list items based on the search input value.
   * Also hides alpha-group dividers when all their items are hidden.
   *
   * @param {Event} event
   */
  #filterItems = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.currentTarget);
    const query = input.value.trim().toLowerCase();
    let anyVisible = false;

    this.querySelectorAll('.market-selector__alpha-group').forEach((group) => {
      let groupHasVisible = false;

      group.querySelectorAll('[role="option"]').forEach((btn) => {
        const label = (btn.getAttribute('aria-label') ?? '').toLowerCase();
        const nameText = (btn.querySelector('.country')?.textContent ?? '').toLowerCase();
        const matches = label.includes(query) || nameText.includes(query);
        /** @type {HTMLElement} */ (btn).hidden = !matches;
        if (matches) groupHasVisible = true;
      });

      /** @type {HTMLElement} */ (group).hidden = !groupHasVisible;
      if (groupHasVisible) anyVisible = true;
    });

    const noResults = /** @type {HTMLElement | null} */ (
      this.querySelector('#market-sector-no-results')
    );
    if (noResults) {
      noResults.hidden = anyVisible || query.length === 0;
    }
  };

  /**
   * Resets the search filter and shows all items and groups.
   */
  #resetFilter = () => {
    const searchInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('.country-filter__input')
    );
    if (searchInput) {
      searchInput.value = '';
    }

    this.querySelectorAll('[role="option"]').forEach((btn) => {
      /** @type {HTMLElement} */ (btn).hidden = false;
    });

    this.querySelectorAll('.market-selector__alpha-group').forEach((group) => {
      /** @type {HTMLElement} */ (group).hidden = false;
    });

    const noResults = /** @type {HTMLElement | null} */ (
      this.querySelector('#market-sector-no-results')
    );
    if (noResults) {
      noResults.hidden = true;
    }
  };
}

if (!customElements.get('market-sector-list')) {
  customElements.define('market-sector-list', MarketSectorList);
}
